// Texas Hold'em (No-Limit) subgame for Classic Card Games.

import { shuffleDeck, cardName, evaluateBestHand, compareHandScores, HandScore } from './cards';

export interface HoldemConfig {
  startingChips: number;
  smallBlind: number;
  bigBlind: number;
}

export const DEFAULT_HOLDEM_CONFIG: HoldemConfig = {
  startingChips: 1000,
  smallBlind: 10,
  bigBlind: 20,
};

export interface PlayerInit {
  id: string;
  name: string;
  isBot?: boolean;
}

export interface HoldemPlayer {
  id: string;
  name: string;
  chips: number;
  holeCards: [number, number] | null;
  betInRound: number;
  totalContributed: number;
  folded: boolean;
  allIn: boolean;
  isBot: boolean;
  eliminated: boolean;
}

export interface HandResultSummary {
  winners: Array<{ playerId: string; playerName: string; amount: number; description: string }>;
  allHoleCards: Record<string, [number, number]>;
}

export interface HoldemState {
  config: HoldemConfig;
  phase: 'preflop' | 'flop' | 'turn' | 'river' | 'showdown' | 'complete';
  handNumber: number;
  players: HoldemPlayer[];
  communityCards: number[];
  pot: number;
  currentBet: number;
  minRaise: number;
  dealerIndex: number;
  playersToAct: string[];   // IDs of players who still need to act this round, in order
  deck: number[];
  rng: number;
  winnerId: string | null;
  lastHandResult: HandResultSummary | null;
}

export type HoldemAction =
  | { type: 'fold'; playerId: string }
  | { type: 'check'; playerId: string }
  | { type: 'call'; playerId: string }
  | { type: 'raise'; playerId: string; amount: number }  // amount = total bet after raise
  | { type: 'allin'; playerId: string }
  | { type: 'nextHand' };

export type HoldemView = HoldemState & { viewPlayerId: string | null };

export type HoldemActionType = 'fold' | 'check' | 'call' | 'raise' | 'allin' | 'nextHand';

// --- Core API ---

export function createGame(config: HoldemConfig, players: PlayerInit[], seed = Date.now()): HoldemState {
  const [deck, rng] = shuffleDeck(seed | 0);

  const holdemPlayers: HoldemPlayer[] = players.map(p => ({
    id: p.id,
    name: p.name,
    chips: config.startingChips,
    holeCards: null,
    betInRound: 0,
    totalContributed: 0,
    folded: false,
    allIn: false,
    isBot: p.isBot ?? false,
    eliminated: false,
  }));

  const base: HoldemState = {
    config,
    phase: 'preflop',
    handNumber: 0,
    players: holdemPlayers,
    communityCards: [],
    pot: 0,
    currentBet: 0,
    minRaise: config.bigBlind,
    dealerIndex: 0,
    playersToAct: [],
    deck,
    rng,
    winnerId: null,
    lastHandResult: null,
  };

  return startHand(base);
}

export function applyAction(state: HoldemState, action: HoldemAction): HoldemState {
  switch (action.type) {
    case 'fold':    return applyBet(state, action.playerId, 'fold');
    case 'check':   return applyBet(state, action.playerId, 'check');
    case 'call':    return applyBet(state, action.playerId, 'call');
    case 'raise':   return applyBet(state, action.playerId, 'raise', action.amount);
    case 'allin':   return applyBet(state, action.playerId, 'allin');
    case 'nextHand':
      return state.phase === 'showdown' ? startNextHand(state) : state;
  }
}

export function getPlayerView(state: HoldemState, playerId: string | null): HoldemView {
  const hidePhases = new Set(['preflop', 'flop', 'turn', 'river']);
  const players = state.players.map(p => ({
    ...p,
    holeCards: playerId !== null && hidePhases.has(state.phase) && p.id !== playerId ? null : p.holeCards,
  }));
  return { ...state, players, viewPlayerId: playerId };
}

export function describe(config: HoldemConfig): string {
  return [
    `Texas Hold'em — No Limit`,
    '',
    `Starting chips: ${config.startingChips}  |  Blinds: ${config.smallBlind}/${config.bigBlind}`,
    '',
    'Each hand: 2 hole cards dealt privately. 5 community cards revealed over 3 rounds.',
    'Betting rounds: Preflop → Flop (3 cards) → Turn (1 card) → River (1 card) → Showdown.',
    'Actions: Fold, Check (if no bet), Call (match bet), Raise (increase bet), All-In.',
    'Best 5-card hand from your 2 hole cards + 5 community cards wins the pot.',
    'Hand ranks (high→low): Straight Flush, Four of a Kind, Full House, Flush, Straight,',
    '  Three of a Kind, Two Pair, One Pair, High Card. Ace plays high and low in straights.',
    'Eliminate all opponents by winning their chips. Last player standing wins.',
  ].join('\n');
}

export function getValidActions(state: HoldemState, playerId: string): HoldemActionType[] {
  if (state.phase === 'showdown') return ['nextHand'];
  if (state.phase === 'complete' || state.playersToAct[0] !== playerId) return [];

  const player = state.players.find(p => p.id === playerId);
  if (!player || player.folded || player.allIn || player.eliminated) return [];

  const actions: HoldemActionType[] = ['fold'];
  const toCall = state.currentBet - player.betInRound;

  if (toCall === 0) actions.push('check');
  else actions.push('call');

  const totalAfterCall = player.betInRound + toCall;
  if (player.chips > toCall) {
    // Can raise: need more chips than the call amount
    actions.push('raise');
  }

  if (player.chips > 0) actions.push('allin');

  return actions;
}

export function getBotAction(playerId: string, state: HoldemState): HoldemAction {
  const player = state.players.find(p => p.id === playerId);
  if (!player?.holeCards) return { type: 'check', playerId };

  const strength = estimateStrength(player.holeCards, state.communityCards);
  const toCall = state.currentBet - player.betInRound;
  const potOdds = toCall > 0 ? toCall / (state.pot + toCall) : 0;
  const valid = getValidActions(state, playerId);

  if (strength > 0.75 && valid.includes('raise')) {
    const target = Math.min(
      state.currentBet + state.minRaise * 3,
      player.chips + player.betInRound
    );
    return { type: 'raise', playerId, amount: target };
  }
  if (strength > potOdds + 0.1 && valid.includes('call')) return { type: 'call', playerId };
  if (valid.includes('check')) return { type: 'check', playerId };
  if (strength > 0.3 && valid.includes('call')) return { type: 'call', playerId };
  return { type: 'fold', playerId };
}

// --- Internal ---

function estimateStrength(holeCards: [number, number], community: number[]): number {
  if (community.length === 0) return preflopStrength(holeCards);
  const { score } = evaluateBestHand([...holeCards, ...community]);
  return score[0] / 8;
}

function preflopStrength([a, b]: [number, number]): number {
  // evalRank: A=12, 2=0, ..., K=11
  const er = (rank: number) => rank === 0 ? 12 : rank - 1;
  const r1 = er(a % 13), r2 = er(b % 13);
  const hi = Math.max(r1, r2), lo = Math.min(r1, r2);
  const suited = Math.floor(a / 13) === Math.floor(b / 13);
  if (r1 === r2) return 0.5 + hi / 24;
  const base = (hi + lo) / 24;
  return Math.min(0.8, base + (suited ? 0.05 : 0) + (hi - lo <= 2 ? 0.05 : 0));
}

function activePlayers(players: HoldemPlayer[]): HoldemPlayer[] {
  return players.filter(p => !p.folded && !p.eliminated);
}

function startHand(state: HoldemState): HoldemState {
  const n = state.players.length;

  // Shuffle fresh deck
  const [deck, rng] = shuffleDeck(state.rng);

  // Reset per-hand fields
  let players = state.players.map(p => ({
    ...p,
    betInRound: 0,
    totalContributed: 0,
    folded: p.eliminated,
    allIn: false,
    holeCards: null as [number, number] | null,
  }));

  // Deal 2 hole cards to each non-eliminated player
  const deckCopy = [...deck];
  for (let i = 0; i < n; i++) {
    const idx = (state.dealerIndex + 1 + i) % n;
    if (!players[idx].eliminated) {
      players[idx] = { ...players[idx], holeCards: [deckCopy.pop()!, deckCopy.pop()!] };
    }
  }

  // Find SB and BB (skip eliminated)
  let sbIdx = (state.dealerIndex + 1) % n;
  while (players[sbIdx].eliminated) sbIdx = (sbIdx + 1) % n;
  let bbIdx = (sbIdx + 1) % n;
  while (players[bbIdx].eliminated) bbIdx = (bbIdx + 1) % n;

  const { smallBlind, bigBlind } = state.config;
  let pot = 0;

  const postBlind = (idx: number, amount: number) => {
    const actual = Math.min(amount, players[idx].chips);
    players[idx] = {
      ...players[idx],
      chips: players[idx].chips - actual,
      betInRound: actual,
      totalContributed: actual,
      allIn: players[idx].chips - actual === 0,
    };
    pot += actual;
  };

  postBlind(sbIdx, smallBlind);
  postBlind(bbIdx, bigBlind);

  // playersToAct: start from UTG (after BB), end at BB (BB gets option if no raise)
  const playersToAct: string[] = [];
  for (let i = 1; i <= n; i++) {
    const idx = (bbIdx + i) % n;
    const p = players[idx];
    if (!p.eliminated && !p.allIn) playersToAct.push(p.id);
  }

  return {
    ...state,
    phase: 'preflop',
    handNumber: state.handNumber + 1,
    players,
    communityCards: [],
    pot,
    currentBet: bigBlind,
    minRaise: bigBlind,
    deck: deckCopy,
    rng,
    playersToAct,
    lastHandResult: null,
  };
}

function startNextHand(state: HoldemState): HoldemState {
  const n = state.players.length;
  let nextDealer = (state.dealerIndex + 1) % n;
  while (state.players[nextDealer].eliminated) nextDealer = (nextDealer + 1) % n;
  return startHand({ ...state, dealerIndex: nextDealer });
}

function buildNextToAct(players: HoldemPlayer[], afterIndex: number): string[] {
  const n = players.length;
  const actorId = players[afterIndex].id;
  const result: string[] = [];
  for (let i = 1; i <= n; i++) {
    const idx = (afterIndex + i) % n;
    const p = players[idx];
    if (!p.folded && !p.allIn && !p.eliminated && p.id !== actorId) {
      result.push(p.id);
    }
  }
  return result;
}

function applyBet(
  state: HoldemState,
  playerId: string,
  betType: 'fold' | 'check' | 'call' | 'raise' | 'allin',
  raiseAmount?: number
): HoldemState {
  if (state.phase === 'complete' || state.phase === 'showdown') return state;
  if (state.playersToAct[0] !== playerId) return state;

  const playerIdx = state.players.findIndex(p => p.id === playerId);
  if (playerIdx === -1) return state;

  let players = [...state.players];
  let { pot, currentBet, minRaise } = state;
  let playersToAct = state.playersToAct.slice(1);

  const p = { ...players[playerIdx] };

  switch (betType) {
    case 'fold': {
      p.folded = true;
      break;
    }
    case 'check': {
      // No-op if valid
      break;
    }
    case 'call': {
      const toCall = Math.min(currentBet - p.betInRound, p.chips);
      p.chips -= toCall;
      p.betInRound += toCall;
      p.totalContributed += toCall;
      pot += toCall;
      if (p.chips === 0) p.allIn = true;
      break;
    }
    case 'raise': {
      const target = raiseAmount ?? currentBet + minRaise;
      const toAdd = Math.min(target - p.betInRound, p.chips);
      p.chips -= toAdd;
      p.betInRound += toAdd;
      p.totalContributed += toAdd;
      pot += toAdd;
      if (p.chips === 0) p.allIn = true;
      if (p.betInRound > currentBet) {
        minRaise = Math.max(minRaise, p.betInRound - currentBet);
        currentBet = p.betInRound;
        playersToAct = buildNextToAct(players, playerIdx);
      }
      break;
    }
    case 'allin': {
      const toAdd = p.chips;
      p.chips = 0;
      p.betInRound += toAdd;
      p.totalContributed += toAdd;
      pot += toAdd;
      p.allIn = true;
      if (p.betInRound > currentBet) {
        minRaise = Math.max(minRaise, p.betInRound - currentBet);
        currentBet = p.betInRound;
        playersToAct = buildNextToAct(players, playerIdx);
      }
      break;
    }
  }

  players[playerIdx] = p;
  let next: HoldemState = { ...state, players, pot, currentBet, minRaise, playersToAct };

  // If only one non-folded player remains, award pot immediately
  const nonFolded = activePlayers(next.players);
  if (nonFolded.length === 1) return awardPot(next, nonFolded[0].id);

  // Advance phase when betting round complete
  if (playersToAct.length === 0) next = advancePhase(next);

  return next;
}

function dealCards(state: HoldemState, count: number): HoldemState {
  const deck = [...state.deck];
  const dealt: number[] = [];
  for (let i = 0; i < count; i++) dealt.push(deck.pop()!);
  return { ...state, deck, communityCards: [...state.communityCards, ...dealt] };
}

function startBettingRound(state: HoldemState): HoldemState {
  const players = state.players.map(p => ({ ...p, betInRound: 0 }));
  const n = players.length;
  const playersToAct: string[] = [];
  for (let i = 1; i <= n; i++) {
    const idx = (state.dealerIndex + i) % n;
    const p = players[idx];
    if (!p.folded && !p.allIn && !p.eliminated) playersToAct.push(p.id);
  }
  return { ...state, players, currentBet: 0, minRaise: state.config.bigBlind, playersToAct };
}

function advancePhase(state: HoldemState): HoldemState {
  const canAct = activePlayers(state.players).filter(p => !p.allIn);
  const needsBetting = canAct.length >= 2;

  if (state.phase === 'preflop') {
    const s = dealCards({ ...state, phase: 'flop' }, 3);
    if (needsBetting) return startBettingRound(s);
    return advancePhase(s);
  }
  if (state.phase === 'flop') {
    const s = dealCards({ ...state, phase: 'turn' }, 1);
    if (needsBetting) return startBettingRound(s);
    return advancePhase(s);
  }
  if (state.phase === 'turn') {
    const s = dealCards({ ...state, phase: 'river' }, 1);
    if (needsBetting) return startBettingRound(s);
    return resolveShowdown(s);
  }
  return resolveShowdown(state);
}

function awardPot(state: HoldemState, winnerId: string): HoldemState {
  let players = state.players.map(p =>
    p.id === winnerId ? { ...p, chips: p.chips + state.pot } : p
  );
  players = players.map(p => ({ ...p, eliminated: p.eliminated || p.chips === 0 }));

  const remaining = players.filter(p => !p.eliminated);
  const gameWinner = remaining.length === 1 ? remaining[0].id : null;
  const winnerName = players.find(p => p.id === winnerId)?.name ?? winnerId;

  return {
    ...state,
    phase: gameWinner ? 'complete' : 'showdown',
    players,
    playersToAct: [],
    winnerId: gameWinner,
    lastHandResult: {
      winners: [{ playerId: winnerId, playerName: winnerName, amount: state.pot, description: 'All others folded' }],
      allHoleCards: {},
    },
  };
}

function computePots(players: HoldemPlayer[]): { amount: number; eligibleIds: string[] }[] {
  const pots: { amount: number; eligibleIds: string[] }[] = [];
  const cs = players.map(p => ({ id: p.id, contrib: p.totalContributed, folded: p.folded }));

  while (cs.some(c => c.contrib > 0)) {
    const min = Math.min(...cs.filter(c => c.contrib > 0).map(c => c.contrib));
    const amount = cs.reduce((s, c) => s + Math.min(c.contrib, min), 0);
    const eligibleIds = cs.filter(c => !c.folded && c.contrib >= min).map(c => c.id);
    if (amount > 0) pots.push({ amount, eligibleIds });
    for (const c of cs) c.contrib = Math.max(0, c.contrib - min);
  }

  return pots;
}

function resolveShowdown(state: HoldemState): HoldemState {
  const community = state.communityCards;
  const pots = computePots(state.players);
  let players = [...state.players];
  const winners: HandResultSummary['winners'] = [];
  const allHoleCards: Record<string, [number, number]> = {};

  for (const p of players) {
    if (p.holeCards && !p.folded) allHoleCards[p.id] = p.holeCards;
  }

  for (const pot of pots) {
    const eligible = pot.eligibleIds
      .map(id => players.find(p => p.id === id)!)
      .filter(p => p.holeCards && !p.folded);

    if (eligible.length === 0) continue;

    const evals = eligible.map(p => ({
      p,
      result: evaluateBestHand([...p.holeCards!, ...community]),
    }));

    const best: HandScore = evals.reduce(
      (b, e) => compareHandScores(e.result.score, b) > 0 ? e.result.score : b,
      evals[0].result.score
    );
    const potWinners = evals.filter(e => compareHandScores(e.result.score, best) === 0);
    const perWinner = Math.floor(pot.amount / potWinners.length);
    const rem = pot.amount % potWinners.length;

    potWinners.forEach((e, i) => {
      const idx = players.findIndex(p => p.id === e.p.id);
      const amt = perWinner + (i === 0 ? rem : 0);
      players[idx] = { ...players[idx], chips: players[idx].chips + amt };
      winners.push({ playerId: e.p.id, playerName: e.p.name, amount: amt, description: e.result.description });
    });
  }

  players = players.map(p => ({ ...p, eliminated: p.eliminated || p.chips === 0 }));
  const remaining = players.filter(p => !p.eliminated);
  const gameWinner = remaining.length === 1 ? remaining[0].id : null;

  return {
    ...state,
    phase: gameWinner ? 'complete' : 'showdown',
    players,
    playersToAct: [],
    winnerId: gameWinner,
    lastHandResult: { winners, allHoleCards },
  };
}
